import { useRef } from 'react'
import { type GradientStop } from '@backend/lib/db/typeRoots/layoutNode'
import { colorToHex } from '@/components/modules/CanvasEditor/color'
import { useCursorStyle } from '@/context/cursorStyleContext'

interface GradientBarProps {
  stops: GradientStop[]
  selectedStopIndex: number
  onSelectStop: (index: number) => void
  onMoveStop: (index: number, position: number, isHoverSelection: boolean) => void
  onAddStop: (position: number) => void
}

export function GradientBar({
  stops,
  selectedStopIndex,
  onSelectStop,
  onMoveStop,
  onAddStop,
}: GradientBarProps) {
  const { startDragging, stopDragging } = useCursorStyle()
  const barRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)
  const dragIndexRef = useRef<number>(-1)

  const sorted = [...stops].sort((a, b) => a.position - b.position)
  const cssStops = sorted.map((s) => `${colorToHex(s.color)} ${s.position * 100}%`).join(', ')

  const getPositionFromEvent = (clientX: number): number => {
    if (!barRef.current) return 0
    const rect = barRef.current.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  }

  const didInteractRef = useRef(false)

  const handleBarClick = (e: React.MouseEvent) => {
    if (didInteractRef.current) {
      didInteractRef.current = false
      return
    }
    const position = getPositionFromEvent(e.clientX)
    onAddStop(position)
  }

  const handleStopMouseDown = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    e.preventDefault()
    didInteractRef.current = true
    onSelectStop(index)

    startDragging('right')
    isDraggingRef.current = true
    dragIndexRef.current = index

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current) return
      moveEvent.preventDefault()
      const position = getPositionFromEvent(moveEvent.clientX)
      onMoveStop(dragIndexRef.current, position, true)
    }

    const handleMouseUp = (upEvent: MouseEvent) => {
      upEvent.preventDefault()
      stopDragging()
      const position = getPositionFromEvent(upEvent.clientX)
      onMoveStop(dragIndexRef.current, position, false)
      isDraggingRef.current = false
      dragIndexRef.current = -1
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  return (
    <div
      ref={barRef}
      className="border-border relative h-6 cursor-crosshair rounded-md border"
      style={{ background: `linear-gradient(90deg, ${cssStops})` }}
      onClick={handleBarClick}>
      {stops.map((stop, index) => (
        <div
          key={index}
          className={`absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 cursor-ew-resize rounded-full border-2 ${
            index === selectedStopIndex
              ? 'border-primary ring-primary/50 ring-2'
              : 'border-background'
          }`}
          style={{
            left: `${stop.position * 100}%`,
            backgroundColor: colorToHex(stop.color),
          }}
          onMouseDown={(e) => handleStopMouseDown(e, index)}
        />
      ))}
    </div>
  )
}
